import { Component, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { projectMilestones, requirementsMilestoneKey } from '@dhats-hub/utils';

/**
 * The four client-facing milestones of a project, read off its status.
 *
 * Lived only in the admin project page; the client portal showed four loose
 * pills instead, each derived from a different field, with no sense of order.
 * Both portals now render this.
 */
@Component({
  selector: 'dhats-project-milestones',
  standalone: true,
  imports: [CommonModule, TranslocoPipe],
  template: `
    <div
      class="py-4 my-2 px-6 rounded-2xl border"
      style="background: var(--glass-bg); border-color: var(--glass-border)"
    >
      @if (heading()) {
        <p
          class="text-[10px] font-black uppercase tracking-[0.25em] mb-4"
          style="color: var(--text-muted)"
        >
          {{ heading() | transloco }}
        </p>
      }
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 relative">
        @for (step of steps(); track step.id) {
          <div class="flex items-center gap-3 relative">
            <div
              class="w-8 h-8 rounded-full flex items-center justify-center font-black text-xs transition-all flex-shrink-0"
              [ngClass]="{
                'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40': step.state === 'completed',
                'bg-blue-500/20 text-blue-400 border-2 border-blue-500 shadow-lg shadow-blue-500/20 ring-2 ring-blue-500/20': step.state === 'current',
                'bg-zinc-500/10 text-zinc-500 border border-zinc-500/20 opacity-50': step.state === 'future'
              }"
            >
              @if (step.state === 'completed') {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                </svg>
              } @else {
                {{ step.id }}
              }
            </div>

            <div class="flex flex-col min-w-0">
              <span
                class="text-xs font-bold tracking-tight truncate"
                [ngClass]="{
                  'text-emerald-400': step.state === 'completed',
                  'text-blue-400 font-extrabold': step.state === 'current',
                  'opacity-40': step.state === 'future'
                }"
                [style.color]="step.state === 'future' ? 'var(--text-muted)' : undefined"
              >
                {{ 'status.' + step.labelKey | transloco }}
              </span>
              @if (step.state === 'current') {
                <!-- "Current", not "In Progress": one of the steps is itself
                     called In Progress, and the two would read as a stutter. -->
                <span class="text-[9px] font-black uppercase tracking-widest text-blue-400 inline-flex items-center gap-1">
                  <span class="w-1.5 h-1.5 rounded-full bg-blue-400 animate-ping"></span>
                  {{ 'milestone.current' | transloco }}
                </span>
              } @else if (step.state === 'completed') {
                <span class="text-[9px] font-black uppercase tracking-widest text-emerald-400/80">
                  {{ 'milestone.completed' | transloco }}
                </span>
              } @else {
                <span class="text-[9px] font-black uppercase tracking-widest opacity-40" style="color: var(--text-muted)">
                  {{ 'milestone.pending' | transloco }}
                </span>
              }
            </div>
          </div>
        }
      </div>
    </div>
  `,
})
export class ProjectMilestonesComponent {
  status = input<string | null | undefined>();
  /** A translation key, not a literal. */
  heading = input<string>('milestone.headingAdmin');

  readonly steps = computed(() => {
    const status = this.status();
    return projectMilestones(status).map((m) => ({
      ...m,
      // Step one is named after how far the requirements have got, which is
      // three different words; the rest are named after their status. Both
      // resolve under the `status.` namespace: every step is named after a
      // point the project passes through.
      labelKey: m.id === 1 ? requirementsMilestoneKey(status) : m.key,
    }));
  });
}
